<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import IconeSvg from '@/components/ui/IconeSvg.vue'
import { useContextoStore } from '@/stores/contexto'
import type { Empresa, Unidade } from '@/types'

/* ---------------------------------------------------------------------------
   POR QUE NÃO É MAIS UM <select>

   O nativo dava conta de uma lista plana de unidades e nada além disso. Três
   coisas que ele não faz, e que a barra superior precisa fazer:

   1. Oferecer a EMPRESA INTEIRA como escolha. Num <select> ela viraria mais uma
      <option> dentro do próprio <optgroup> que a nomeia — "MOV TELECOM" acima de
      "MOV TELECOM (todas)" —, que é exatamente a lista que ninguém entende.
   2. Mostrar a cor da empresa em cada linha. <option> não aceita estilo confiável:
      o popup é desenhado pelo sistema, não pelo CSS da página (a mesma armadilha
      que já custou tempo com o tema escuro).
   3. Buscar. Com três empresas e dezoito unidades, rolar uma lista alfabética é
      mais lento que digitar "itai".

   O preço é reimplementar o que o nativo dá de graça: teclado, foco e fechamento.
   Está tudo abaixo, e é o motivo de este arquivo não ser curto.
--------------------------------------------------------------------------- */

const contexto = useContextoStore()

const aberto = ref(false)
const busca = ref('')

const raiz = ref<HTMLElement | null>(null)
const gatilho = ref<HTMLButtonElement | null>(null)
const painel = ref<HTMLElement | null>(null)
const campoDeBusca = ref<HTMLInputElement | null>(null)

/** A busca só aparece quando há o que procurar; abaixo disso ela é ruído. */
const buscaUtil = computed(() => contexto.unidades.length + contexto.empresas.length > 8)

interface EmpresaFiltrada extends Empresa {
  unidadesVisiveis: Unidade[]
}

/**
 * O filtro casa com empresa OU unidade, e mantém a empresa na lista quando o termo
 * bate no nome dela.
 *
 * Sem essa segunda metade, digitar "fibra" esconderia a linha "XT FIBRA — empresa
 * inteira", que é justamente a que a pessoa estava procurando.
 */
const empresasFiltradas = computed<EmpresaFiltrada[]>(() => {
  const termo = busca.value.trim().toLowerCase()

  return contexto.empresas
    .map((empresa) => {
      const empresaCasa = termo === '' || empresa.nome.toLowerCase().includes(termo)

      const unidadesVisiveis = empresaCasa
        ? empresa.unidades
        : empresa.unidades.filter((u) =>
            [u.nome, u.cidade, u.uf, u.codigo].join(' ').toLowerCase().includes(termo),
          )

      return { ...empresa, unidadesVisiveis }
    })
    .filter((e) => e.unidadesVisiveis.length > 0 || e.nome.toLowerCase().includes(termo))
})

const nadaEncontrado = computed(
  () => busca.value.trim() !== '' && empresasFiltradas.value.length === 0,
)

/** A régua do gatilho: cor sólida com empresa em foco, listrada no consolidado. */
const reguaDoGatilho = computed(() => {
  if (contexto.empresaAtual) return { background: contexto.empresaAtual.cor }

  const cores = contexto.empresas.map((e) => e.cor)
  if (cores.length === 0) return { background: 'var(--borda)' }

  return { backgroundImage: `linear-gradient(180deg, ${cores.join(', ')})` }
})

/**
 * A segunda linha do gatilho: onde exatamente se está.
 *
 * Cor nunca carrega informação sozinha neste sistema, e esta linha é a metade
 * escrita do sinal. Ela diz coisas diferentes nos três focos, porque as três
 * situações são diferentes: uma cidade, uma contagem de unidades, ou o grupo.
 */
const detalhe = computed(() => {
  const u = contexto.unidadeAtual
  if (u) return `${u.cidade}/${u.uf}`

  if (contexto.empresaAtual) {
    const n = contexto.empresaAtual.unidades.length
    return n === 1 ? '1 unidade' : `${n} unidades`
  }

  const n = contexto.empresas.length
  return n === 1 ? 'Consolidado' : `Consolidado · ${n} empresas`
})

const titulo = computed(() =>
  contexto.unidadeAtual
    ? contexto.unidadeAtual.nome
    : (contexto.empresaAtual?.nome ?? 'Todas as minhas unidades'),
)

/* ------------------------------------------------------------ abrir/fechar */

async function abrir(): Promise<void> {
  aberto.value = true
  busca.value = ''

  await nextTick()

  // O foco vai para a busca quando ela existe, e para o primeiro item quando não.
  // Abrir um menu sem mover o foco deixa quem usa teclado preso do lado de fora.
  if (buscaUtil.value) campoDeBusca.value?.focus()
  else itens()[0]?.focus()
}

function fechar(devolverFoco = true): void {
  if (!aberto.value) return

  aberto.value = false

  // Devolver o foco ao gatilho é o que impede o teclado de voltar ao começo da
  // página a cada fechamento.
  if (devolverFoco) gatilho.value?.focus()
}

function alternar(): void {
  if (aberto.value) fechar()
  else void abrir()
}

/** Os itens focáveis do painel, na ordem em que aparecem na tela. */
function itens(): HTMLButtonElement[] {
  return Array.from(
    painel.value?.querySelectorAll<HTMLButtonElement>('[data-item]') ?? [],
  )
}

function mover(passo: number): void {
  const lista = itens()
  if (lista.length === 0) return

  const atual = lista.indexOf(document.activeElement as HTMLButtonElement)

  // De -1 (foco na busca) o passo para baixo cai no primeiro item e o passo para
  // cima cai no último. A lista dá a volta nas duas pontas.
  if (atual === -1) {
    lista[passo < 0 ? lista.length - 1 : 0]?.focus()
    return
  }

  lista[(atual + passo + lista.length) % lista.length]?.focus()
}

function aoTeclar(evento: KeyboardEvent): void {
  if (evento.key === 'Escape') {
    evento.stopPropagation()
    fechar()
    return
  }

  if (evento.key === 'ArrowDown') {
    evento.preventDefault()
    mover(1)
    return
  }

  if (evento.key === 'ArrowUp') {
    evento.preventDefault()
    mover(-1)
    return
  }

  if (evento.key === 'Home' || evento.key === 'End') {
    evento.preventDefault()
    const lista = itens()
    ;(evento.key === 'Home' ? lista[0] : lista[lista.length - 1])?.focus()
  }
}

/**
 * Clique fora fecha — e o `pointerdown` é deliberado.
 *
 * Com `click`, arrastar uma seleção de texto de dentro do painel para fora fecharia
 * o menu no soltar do botão. Com `pointerdown` o teste é feito onde o gesto começou.
 */
function aoApontarFora(evento: PointerEvent): void {
  if (!aberto.value) return
  if (raiz.value?.contains(evento.target as Node)) return

  fechar(false)
}

onMounted(() => document.addEventListener('pointerdown', aoApontarFora))
onBeforeUnmount(() => document.removeEventListener('pointerdown', aoApontarFora))

// Trocar de contexto por outro caminho (um link do painel, por exemplo) precisa
// fechar o menu: um painel aberto mostrando o foco antigo contradiz a tela.
watch(
  () => contexto.versao,
  () => fechar(false),
)

/* --------------------------------------------------------------- escolher */

async function escolher(empresa: number | null, unidade: number | null): Promise<void> {
  fechar()
  await contexto.focar({ empresa, unidade })
}

const empresaEmFoco = (id: number): boolean =>
  contexto.empresaSelecionada === id && contexto.unidadeSelecionada === null

const unidadeEmFoco = (id: number): boolean => contexto.unidadeSelecionada === id
</script>

<template>
  <div v-if="contexto.seletorRelevante" ref="raiz" class="seletor">
    <button
      ref="gatilho"
      type="button"
      class="gatilho"
      :aria-expanded="aberto"
      aria-haspopup="menu"
      @click="alternar"
      @keydown.down.prevent="aberto ? mover(1) : abrir()"
    >
      <!-- Barra vertical na cor da empresa: identifica o contexto pela visão
           periférica, sem ocupar espaço de leitura. -->
      <span class="gatilho__regua" :style="reguaDoGatilho" aria-hidden="true" />

      <span class="gatilho__texto">
        <span class="gatilho__rotulo">Contexto</span>
        <span class="gatilho__titulo">{{ titulo }}</span>
        <span class="gatilho__detalhe">{{ detalhe }}</span>
      </span>

      <IconeSvg
        nome="seta"
        :tamanho="14"
        class="gatilho__seta"
        :class="{ 'gatilho__seta--aberta': aberto }"
      />
    </button>

    <div
      v-if="aberto"
      ref="painel"
      class="painel"
      role="menu"
      aria-label="Escolher empresa ou unidade"
      @keydown="aoTeclar"
    >
      <div v-if="buscaUtil" class="painel__busca">
        <IconeSvg nome="busca" :tamanho="14" />
        <input
          ref="campoDeBusca"
          v-model="busca"
          type="search"
          class="painel__campo"
          placeholder="Buscar empresa ou unidade"
          aria-label="Buscar empresa ou unidade"
        />
      </div>

      <div class="painel__lista">
        <!-- O consolidado primeiro e sempre visível, mesmo com busca ativa: é o
             caminho de volta, e caminho de volta que some é armadilha. -->
        <button
          type="button"
          data-item
          role="menuitemradio"
          class="opcao opcao--consolidado"
          :aria-checked="contexto.consolidado"
          @click="escolher(null, null)"
        >
          <span class="opcao__marca opcao__marca--todas" aria-hidden="true">
            <span
              v-for="e in contexto.empresas"
              :key="e.id"
              :style="{ background: e.cor }"
            />
          </span>
          <span class="opcao__texto">
            <span class="opcao__nome">Todas as minhas unidades</span>
            <span class="opcao__nota">o grupo inteiro, dentro do seu acesso</span>
          </span>
          <IconeSvg v-if="contexto.consolidado" nome="confere" :tamanho="15" />
        </button>

        <div
          v-for="empresa in empresasFiltradas"
          :key="empresa.id"
          class="grupo"
          role="group"
          :aria-label="empresa.nome"
        >
          <!-- A empresa é uma ESCOLHA, não um cabeçalho decorativo. Era o que
               faltava: focar a empresa inteira sem percorrer unidade a unidade. -->
          <button
            type="button"
            data-item
            role="menuitemradio"
            class="opcao opcao--empresa"
            :aria-checked="empresaEmFoco(empresa.id)"
            :style="{ '--cor-empresa': empresa.cor }"
            @click="escolher(empresa.id, null)"
          >
            <span class="opcao__marca opcao__marca--empresa" aria-hidden="true" />
            <span class="opcao__texto">
              <span class="opcao__nome">{{ empresa.nome }}</span>
              <span class="opcao__nota">
                {{
                  empresa.unidades.length === 1
                    ? 'empresa inteira · 1 unidade'
                    : `empresa inteira · ${empresa.unidades.length} unidades`
                }}
              </span>
            </span>
            <IconeSvg v-if="empresaEmFoco(empresa.id)" nome="confere" :tamanho="15" />
          </button>

          <button
            v-for="unidade in empresa.unidadesVisiveis"
            :key="unidade.id"
            type="button"
            data-item
            role="menuitemradio"
            class="opcao opcao--unidade"
            :aria-checked="unidadeEmFoco(unidade.id)"
            :style="{ '--cor-empresa': empresa.cor }"
            @click="escolher(empresa.id, unidade.id)"
          >
            <span class="opcao__marca opcao__marca--unidade" aria-hidden="true" />
            <span class="opcao__texto">
              <span class="opcao__nome">{{ unidade.nome }}</span>
              <span class="opcao__nota numero">
                {{ unidade.cidade }}/{{ unidade.uf }} · {{ unidade.codigo }}
              </span>
            </span>
            <IconeSvg v-if="unidadeEmFoco(unidade.id)" nome="confere" :tamanho="15" />
          </button>
        </div>

        <p v-if="nadaEncontrado" class="painel__vazio">
          Nada encontrado para “{{ busca }}”.
        </p>
      </div>
    </div>
  </div>

  <!-- Com uma empresa e uma unidade só não há o que escolher, mas mostrar onde se
       está continua valendo: evita a dúvida de "esta tela é de qual unidade?". -->
  <p v-else-if="contexto.unidades.length === 1" class="seletor seletor--fixo">
    <span
      class="gatilho__regua"
      :style="{ background: contexto.empresas[0]?.cor }"
      aria-hidden="true"
    />
    <span class="gatilho__texto">
      <span class="gatilho__rotulo">Contexto</span>
      <span class="gatilho__titulo">{{ contexto.unidades[0].nome }}</span>
      <span class="gatilho__detalhe">
        {{ contexto.unidades[0].cidade }}/{{ contexto.unidades[0].uf }}
      </span>
    </span>
  </p>
</template>

<style scoped>
.seletor {
  position: relative;
  margin: 0;
}

.seletor--fixo {
  display: flex;
  align-items: stretch;
  gap: var(--e-3);
  padding: var(--e-2) var(--e-1);
}

/* ----------------------------------------------------------------- gatilho */

.gatilho {
  display: flex;
  align-items: stretch;
  gap: var(--e-3);
  /* 44px de alvo, como todo controle do sistema. */
  min-height: 44px;
  max-width: 22rem;
  padding: var(--e-2) var(--e-3);
  border: 1px solid transparent;
  border-radius: var(--raio);
  background: none;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition:
    background-color var(--transicao),
    border-color var(--transicao);
}

.gatilho:hover,
.gatilho[aria-expanded='true'] {
  border-color: var(--borda);
  background: var(--superficie);
}

.gatilho__regua {
  width: 3px;
  border-radius: 2px;
  flex: none;
}

.gatilho__texto {
  display: grid;
  gap: 1px;
  min-width: 0;
}

.gatilho__rotulo {
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  color: var(--texto-fraco);
}

.gatilho__titulo {
  font-size: var(--t-base);
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.gatilho__detalhe {
  font-size: var(--t-xs);
  color: var(--texto-fraco);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.gatilho__seta {
  align-self: center;
  flex: none;
  color: var(--texto-fraco);
  transform: rotate(90deg);
  transition: transform var(--transicao);
}

.gatilho__seta--aberta {
  transform: rotate(-90deg);
}

/* ------------------------------------------------------------------ painel */

.painel {
  position: absolute;
  top: calc(100% + var(--e-2));
  left: 0;
  z-index: var(--z-flutuante);
  width: min(24rem, calc(100vw - var(--e-6)));
  border: 1px solid var(--borda);
  border-radius: var(--raio-lg);
  background: var(--superficie);
  box-shadow: var(--sombra-3);
  overflow: hidden;
  animation: surgir 180ms cubic-bezier(0.32, 0.72, 0, 1);
}

@keyframes surgir {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .painel {
    animation: none;
  }
}

.painel__busca {
  display: flex;
  align-items: center;
  gap: var(--e-2);
  padding: var(--e-2) var(--e-3);
  border-bottom: 1px solid var(--borda);
  color: var(--texto-fraco);
}

.painel__campo {
  flex: 1;
  min-width: 0;
  min-height: 32px;
  border: 0;
  background: none;
  color: var(--texto);
  font: inherit;
  font-size: var(--t-base);
}

.painel__campo:focus {
  outline: none;
}

.painel__lista {
  /* Teto de altura com rolagem: dezoito unidades passariam do rodapé da janela. */
  max-height: min(28rem, 60vh);
  overflow-y: auto;
  padding: var(--e-2);
}

.painel__vazio {
  margin: 0;
  padding: var(--e-4) var(--e-3);
  color: var(--texto-suave);
  font-size: var(--t-sm);
}

.grupo + .grupo,
.opcao--consolidado + .grupo {
  margin-top: var(--e-1);
  padding-top: var(--e-1);
  border-top: 1px solid var(--borda);
}

/* ------------------------------------------------------------------ opções */

.opcao {
  display: flex;
  align-items: center;
  gap: var(--e-3);
  width: 100%;
  min-height: 44px;
  padding: var(--e-2);
  border: 0;
  border-radius: var(--raio);
  background: none;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background-color var(--transicao);
}

.opcao:hover {
  background: var(--superficie-2);
}

.opcao[aria-checked='true'] {
  background: var(--superficie-2);
  font-weight: 500;
}

.opcao__texto {
  display: grid;
  gap: 1px;
  /* Item de grade nasce com min-width: auto — um nome longo estabelece um piso
     maior que a coluna e transborda por cima do ícone de conferido. */
  min-width: 0;
  flex: 1;
}

.opcao__nome {
  font-size: var(--t-base);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.opcao__nota {
  font-size: var(--t-xs);
  color: var(--texto-fraco);
}

.opcao__marca {
  flex: none;
  border-radius: 2px;
}

.opcao__marca--empresa {
  width: 4px;
  height: 26px;
  background: var(--cor-empresa);
}

/* A unidade herda a cor da empresa, mais estreita e mais baixa: a hierarquia é
   lida antes do recuo, e o recuo sozinho some no meio de uma lista longa. */
.opcao__marca--unidade {
  width: 4px;
  height: 14px;
  margin-left: var(--e-4);
  background: var(--cor-empresa);
  opacity: 0.5;
}

.opcao__marca--todas {
  display: flex;
  flex-direction: column;
  width: 4px;
  height: 26px;
  overflow: hidden;
}

.opcao__marca--todas > span {
  flex: 1;
}

.opcao--unidade .opcao__nome {
  font-size: var(--t-sm);
}

.opcao svg {
  flex: none;
  color: var(--marca);
}

@media (max-width: 40rem) {
  .painel {
    /* No celular o painel encosta na borda direita da tela em vez de vazar. */
    left: auto;
    right: 0;
  }
}
</style>
